import { useState, useRef } from 'react';
import { Plus, Trash2, Pencil, GripVertical, Shield, Sparkles } from 'lucide-react';
import { Tooltip } from '@mui/material';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { TAREFAS_CONFIG_DESIGN } from './TarefasConfig.design';
import ModalWorkflow from './ModalWorkflow';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import {
  actionCriarWorkflow,
  actionEditarWorkflow,
  actionDeletarWorkflow,
  actionReordenarWorkflows,
} from 'src/actions/tarefas/actionTarefasConfig';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';

const TABLE_HEAD = [
  { id: 'ordem', label: '', className: 'w-10' },
  { id: 'nome', label: 'Nome', className: 'flex-1' },
  { id: 'estagios', label: 'Estágios', className: 'w-48' },
  { id: 'tipo', label: 'Tipo', className: 'w-32 text-center' },
  { id: 'actions', label: 'Ações', className: 'w-24 text-right' },
];

export default function WorkflowsTab({ listaWorkflows, onRefresh, clienteId }) {
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [erroDelete, setErroDelete] = useState('');

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const totais = {
    total: listaWorkflows.length,
    ativos: listaWorkflows.filter(w => w.ativo !== false).length,
    padrao: listaWorkflows.filter(w => w.tipo === 'padrao').length,
    custom: listaWorkflows.filter(w => w.tipo === 'custom').length,
  };

  function resetForm() {
    setSelectedWorkflow(null);
    setAction('');
  }

  async function handleCriar(data) {
    try {
      setLoading(true);
      await actionCriarWorkflow({ ...data, cliente_id: clienteId });
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditar(data) {
    try {
      setLoading(true);
      await actionEditarWorkflow(selectedWorkflow._id, data);
      await onRefresh();
      setOpenModal(false);
      resetForm();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeletar() {
    try {
      setLoading(true);
      setErroDelete('');
      await actionDeletarWorkflow(selectedWorkflow._id);
      await onRefresh();
      setOpenConfirmacao(false);
      resetForm();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Erro ao deletar workflow.';
      setErroDelete(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleDragEnd() {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...listaWorkflows];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    dragItem.current = null;
    dragOverItem.current = null;

    try {
      await actionReordenarWorkflows({ ids: reordered.map(w => w._id) });
      await onRefresh();
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Action button */}
      <div className="flex justify-end">
        <button
          onClick={() => { setOpenModal(true); setAction('Criar'); setSelectedWorkflow(null); }}
          className={TAREFAS_CONFIG_DESIGN.page.actionButton}
        >
          <Plus size={18} />
          <span>Criar Workflow</span>
        </button>
      </div>

      {/* Modals */}
      <ModalConfimacao
        open={openConfirmacao}
        onClose={() => { setOpenConfirmacao(false); setErroDelete(''); }}
        onConfirm={handleDeletar}
        loadingFinalizar={loading}
        mensagem="Deseja mesmo excluir esse workflow?"
        erro={erroDelete}
      />
      <ModalWorkflow
        open={openModal}
        action={action}
        workflow={selectedWorkflow}
        onSubmit={action === 'Criar' ? handleCriar : handleEditar}
        onClose={() => { setOpenModal(false); resetForm(); }}
      />

      {/* Table */}
      {listaWorkflows.length === 0 ? (
        <div className={TAREFAS_CONFIG_DESIGN.emptyState.container}>
          <div className={TAREFAS_CONFIG_DESIGN.emptyState.iconBox}>
            <Shield size={32} />
          </div>
          <h3 className={TAREFAS_CONFIG_DESIGN.emptyState.title}>Nenhum workflow encontrado</h3>
          <p className={TAREFAS_CONFIG_DESIGN.emptyState.description}>
            O workflow padrão será criado automaticamente ao carregar a página.
          </p>
        </div>
      ) : (
        <GenericTable headLabels={TABLE_HEAD}>
          {listaWorkflows.map((row, index) => (
            <div
              key={row._id}
              className={`${GENERIC_TABLE_DESIGN.row.container} cursor-grab`}
              draggable
              onDragStart={() => { dragItem.current = index; }}
              onDragEnter={() => { dragOverItem.current = index; }}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
            >
              {/* Drag handle */}
              <div className="w-10 flex items-center text-gray-300">
                <GripVertical size={16} />
              </div>

              {/* Nome */}
              <div className="flex-1 min-w-[200px] pr-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-gray-900">{row.nome}</span>
                  {row.descricao && (
                    <span className="text-xs text-gray-400 line-clamp-1">{row.descricao}</span>
                  )}
                </div>
              </div>

              {/* Estágios */}
              <div className="w-48">
                <div className="flex items-center gap-1 flex-wrap">
                  {(row.estagios || []).sort((a, b) => a.ordem - b.ordem).map((e, i) => (
                    <Tooltip key={i} title={e.nome}>
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0 border border-white shadow-sm"
                        style={{ backgroundColor: e.cor }}
                      />
                    </Tooltip>
                  ))}
                  <span className="text-xs text-gray-400 ml-1">
                    {(row.estagios || []).length} estágios
                  </span>
                </div>
              </div>

              {/* Tipo */}
              <div className="w-32 text-center flex justify-center">
                {row.tipo === 'padrao' ? (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-blue-50 text-blue-700 border-blue-200">
                    <Shield size={10} />
                    Padrão
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-violet-50 text-violet-700 border-violet-200">
                    <Sparkles size={10} />
                    Custom
                  </Badge>
                )}
              </div>

              {/* Actions */}
              <div className="w-24 flex justify-end gap-1">
                <Tooltip title="Editar">
                  <button
                    onClick={() => {
                      setOpenModal(true);
                      setAction('Editar');
                      setSelectedWorkflow(row);
                    }}
                    className="p-2 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-all"
                  >
                    <Pencil size={18} />
                  </button>
                </Tooltip>
                <Tooltip title={row.tipo === 'padrao' ? 'Workflow padrão não pode ser excluído' : 'Excluir'}>
                  <span>
                    <button
                      onClick={() => {
                        if (row.tipo === 'padrao') return;
                        setSelectedWorkflow(row);
                        setOpenConfirmacao(true);
                      }}
                      disabled={row.tipo === 'padrao'}
                      className={`${GENERIC_TABLE_DESIGN.common.deleteButton} ${row.tipo === 'padrao' ? 'opacity-30 cursor-not-allowed' : ''}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
          ))}
        </GenericTable>
      )}
    </div>
  );
}
